import * as React from 'react';
class MemoryManager {
    constructor() {
        this.trackedData = new Map();
        this.referenceCleanupCallbacks = [];
        this.peakUsage = 0;
        this.totalAllocations = 0;
        this.totalReleases = 0;
        this.isMonitoringPaused = false;
        this.thresholds = {
            warning: 150, // 150MB - Reduced to prevent critical usage
            critical: 250, // 250MB - Reduced to prevent crashes
            maxFileSize: 25 // 25MB - Reduced to prevent memory issues
        };
        // Monitor memory usage periodically
        this.startMemoryMonitoring();
    }
    /**
     * Start memory monitoring
     */
    startMemoryMonitoring() {
        if (typeof window !== 'undefined' && 'memory' in performance) {
            setInterval(() => {
                // Skip monitoring if paused
                if (this.isMonitoringPaused) {
                    return;
                }
                const usage = this.getCurrentMemoryUsage();
                // Update peak usage
                if (usage.used > this.peakUsage) {
                    this.peakUsage = usage.used;
                }
                // Check thresholds with more aggressive cleanup
                if (usage.used > this.thresholds.critical * 1024 * 1024) {
                    console.warn('🚨 Critical memory usage detected:', {
                        used: Math.round(usage.used / 1024 / 1024) + 'MB',
                        percentage: Math.round(usage.percentage) + '%'
                    });
                    this.handleCriticalMemoryUsage();
                }
                else if (usage.used > this.thresholds.warning * 1024 * 1024) {
                    console.warn('⚠️ Warning memory usage detected:', {
                        used: Math.round(usage.used / 1024 / 1024) + 'MB',
                        percentage: Math.round(usage.percentage) + '%'
                    });
                    this.handleWarningMemoryUsage();
                }
            }, 5000); // Check every 5 seconds for more responsive monitoring
        }
    }
    /**
     * Get current memory usage
     */
    getCurrentMemoryUsage() {
        if (typeof window !== 'undefined' && 'memory' in performance) {
            const memory = performance.memory;
            const used = memory.usedJSHeapSize;
            const total = memory.totalJSHeapSize;
            const limit = memory.jsHeapSizeLimit;
            return {
                used,
                total,
                limit,
                percentage: (used / limit) * 100
            };
        }
        return {
            used: 0,
            total: 0,
            limit: 0,
            percentage: 0
        };
    }
    /**
     * Pause memory monitoring during critical operations
     */
    pauseMonitoring() {
        this.isMonitoringPaused = true;
    }
    /**
     * Resume memory monitoring
     */
    resumeMonitoring() {
        this.isMonitoringPaused = false;
    }
    /**
     * Check if memory is suitable for quiz generation
     */
    getQuizGenerationMemoryStatus() {
        const usage = this.getCurrentMemoryUsage();
        const usedMB = usage.used / (1024 * 1024);
        if (usedMB < this.thresholds.warning) {
            return {
                suitable: true,
                currentUsage: usedMB,
                recommendation: 'Memory usage is optimal for quiz generation'
            };
        }
        else if (usedMB < this.thresholds.critical) {
            return {
                suitable: true,
                currentUsage: usedMB,
                recommendation: 'Memory usage is acceptable, but consider cleanup after generation'
            };
        }
        else {
            return {
                suitable: false,
                currentUsage: usedMB,
                recommendation: 'Memory usage is too high for quiz generation. Please close other tabs or refresh the page.'
            };
        }
    }
    /**
     * Track PDF data allocation
     */
    trackPdfData(data, identifier) {
        const id = identifier || `pdf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.trackedData.set(id, {
            size: data.byteLength,
            timestamp: Date.now(),
            type: 'pdf'
        });
        this.totalAllocations++;
        // Log allocation
        return id;
    }
    /**
     * Release PDF data
     */
    releasePdfData(data, identifier) {
        const id = identifier || this.findDataById(data);
        if (id && this.trackedData.has(id)) {
            const info = this.trackedData.get(id);
            this.trackedData.delete(id);
            this.totalReleases++;
            // Log release
            return true;
        }
        return false;
    }
    /**
     * Find data by ArrayBuffer reference
     */
    findDataById(data) {
        const entries = Array.from(this.trackedData.entries());
        for (const [id, info] of entries) {
            // This is a simplified check - in a real implementation you'd need a more robust way
            // to track ArrayBuffer references
            if (info.type === 'pdf') {
                return id;
            }
        }
        return null;
    }
    /**
     * Check if file size is acceptable for current memory state
     */
    canHandleFileSize(fileSize) {
        const currentUsage = this.getCurrentMemoryUsage();
        const fileSizeMB = fileSize / (1024 * 1024);
        // Check if file is too large
        if (fileSizeMB > this.thresholds.maxFileSize) {
            return {
                allowed: false,
                reason: `File size (${fileSizeMB.toFixed(1)}MB) exceeds maximum allowed size (${this.thresholds.maxFileSize}MB)`
            };
        }
        // Check if adding this file would exceed critical threshold
        const projectedUsage = currentUsage.used + fileSize;
        if (projectedUsage > this.thresholds.critical * 1024 * 1024) {
            return {
                allowed: false,
                reason: `Adding this file would exceed critical memory threshold (${this.thresholds.critical}MB)`
            };
        }
        return { allowed: true };
    }
    /**
     * Register a callback invoked on memory-warning events (reference viewer LRU, etc.).
     */
    registerReferenceCleanup(callback) {
        this.referenceCleanupCallbacks.push(callback);
        return () => {
            this.referenceCleanupCallbacks = this.referenceCleanupCallbacks.filter((cb) => cb !== callback);
        };
    }
    runReferenceCleanups(level) {
        for (const cb of this.referenceCleanupCallbacks) {
            try {
                cb();
            }
            catch (e) {
                console.warn('Memory Manager: reference cleanup callback failed:', e);
            }
        }
        if (level === 'critical' || level === 'warning') {
            // level is passed for future differentiated cleanup; callbacks read event detail.
        }
    }
    trackBodyHtml(referenceId, byteLength) {
        const id = `body_html_${referenceId}`;
        this.trackedData.set(id, {
            size: Math.max(0, byteLength),
            timestamp: Date.now(),
            type: 'body_html',
        });
        this.totalAllocations++;
        return id;
    }
    releaseBodyHtml(referenceId) {
        const id = `body_html_${referenceId}`;
        if (this.trackedData.has(id)) {
            this.trackedData.delete(id);
            this.totalReleases++;
            return true;
        }
        return false;
    }
    optimizeForReferenceViewer() {
        this.cleanupOldData();
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.clear();
        }
        if (typeof window !== 'undefined' && 'gc' in window) {
            window.gc?.();
        }
    }
    getReferenceViewerMemoryStatus() {
        return this.getQuizGenerationMemoryStatus();
    }
    /**
     * Handle critical memory usage
     */
    handleCriticalMemoryUsage() {
        console.warn('🚨 Memory Manager: Critical memory usage detected - initiating emergency cleanup');
        this.runReferenceCleanups('critical');
        // Clear all tracked data immediately
        this.trackedData.clear();
        // Force garbage collection if available
        if (typeof window !== 'undefined' && window.gc) {
            window.gc();
        }
        // Clear browser caches more aggressively
        if (typeof window !== 'undefined' && 'caches' in window) {
            caches.keys().then(names => {
                names.forEach(name => {
                    caches.delete(name); // Clear all caches, not just specific ones
                });
            });
        }
        // Clear localStorage items more aggressively
        if (typeof window !== 'undefined' && window.localStorage) {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.includes('temp') || key.includes('cache') || key.includes('quiz') ||
                    key.includes('pdf') || key.includes('upload') || key.includes('lesson')) {
                    localStorage.removeItem(key);
                }
            });
        }
        // Clear sessionStorage
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.clear();
        }
        // Clear any blob URLs that might be hanging around
        if (typeof window !== 'undefined') {
            // This is a best-effort cleanup of blob URLs
            const links = document.querySelectorAll('a[href^="blob:"]');
            links.forEach(link => {
                const href = link.getAttribute('href');
                if (href) {
                    URL.revokeObjectURL(href);
                }
            });
        }
        // Notify user or trigger additional cleanup
        this.notifyMemoryWarning('critical');
        // Schedule another cleanup check with shorter interval
        setTimeout(() => {
            const usage = this.getCurrentMemoryUsage();
            if (usage.used > this.thresholds.critical * 1024 * 1024) {
                console.warn('🚨 Memory Manager: Still critical after cleanup, forcing additional cleanup');
                this.forceMemoryCleanup();
            }
        }, 500); // Reduced from 1000ms to 500ms
    }
    /**
     * Force additional memory cleanup
     */
    forceMemoryCleanup() {
        // Clear all tracked data
        this.trackedData.clear();
        // Reset counters
        this.totalAllocations = 0;
        this.totalReleases = 0;
        // Force another garbage collection
        if (typeof window !== 'undefined' && 'gc' in window) {
            window.gc();
        }
    }
    /**
     * Handle warning memory usage
     */
    handleWarningMemoryUsage() {
        console.warn('Memory Manager: High memory usage detected - initiating proactive cleanup');
        this.runReferenceCleanups('warning');
        // Start cleanup earlier to prevent reaching critical levels
        this.cleanupOldData();
        // Clear some cached data proactively
        if (typeof window !== 'undefined' && window.localStorage) {
            const keys = Object.keys(localStorage);
            const tempKeys = keys.filter(key => key.includes('temp') || key.includes('cache'));
            // Remove oldest temp/cache items first
            tempKeys.slice(0, Math.floor(tempKeys.length / 2)).forEach(key => {
                localStorage.removeItem(key);
            });
        }
        // Notify user or trigger additional cleanup
        this.notifyMemoryWarning('warning');
        // Schedule a follow-up check
        setTimeout(() => {
            const usage = this.getCurrentMemoryUsage();
            if (usage.used > this.thresholds.warning * 1024 * 1024) {
                console.warn('Memory Manager: Still high after cleanup, escalating to critical cleanup');
                this.handleCriticalMemoryUsage();
            }
        }, 5000);
    }
    /**
     * Clean up old tracked data
     */
    cleanupOldData() {
        const now = Date.now();
        const maxAge = 30 * 60 * 1000; // 30 minutes
        Array.from(this.trackedData.entries()).forEach(([id, info]) => {
            if (now - info.timestamp > maxAge) {
                this.trackedData.delete(id);
            }
        });
    }
    /**
     * Notify about memory warnings
     */
    notifyMemoryWarning(level) {
        // In a real implementation, you might dispatch a custom event
        // or call a callback to notify the UI
        const event = new CustomEvent('memory-warning', {
            detail: {
                level,
                usage: this.getCurrentMemoryUsage(),
                thresholds: this.thresholds
            }
        });
        if (typeof window !== 'undefined') {
            window.dispatchEvent(event);
        }
    }
    /**
     * Get memory statistics
     */
    getStats() {
        const currentUsage = this.getCurrentMemoryUsage();
        const totalSize = Array.from(this.trackedData.values()).reduce((sum, info) => sum + info.size, 0);
        const averageFileSize = this.trackedData.size > 0 ? totalSize / this.trackedData.size : 0;
        return {
            currentUsage,
            peakUsage: this.peakUsage,
            totalAllocations: this.totalAllocations,
            totalReleases: this.totalReleases,
            averageFileSize
        };
    }
    /**
     * Update memory thresholds
     */
    updateThresholds(newThresholds) {
        this.thresholds = { ...this.thresholds, ...newThresholds };
    }
    /**
     * Force cleanup of all tracked data
     */
    forceCleanup() {
        const count = this.trackedData.size;
        this.trackedData.clear();
    }
    /**
     * Format bytes to human readable string
     */
    formatBytes(bytes) {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
    /**
     * Get list of tracked data
     */
    getTrackedData() {
        return Array.from(this.trackedData.entries()).map(([id, info]) => ({
            id,
            ...info
        }));
    }
    /**
     * Optimize memory for quiz generation
     */
    optimizeForQuizGeneration() {
        // Clear any existing quiz-related cached data
        if (typeof window !== 'undefined' && window.localStorage) {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.includes('quiz') || key.includes('question') || key.includes('temp')) {
                    localStorage.removeItem(key);
                }
            });
        }
        // Clear session storage
        if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.clear();
        }
        // Clear old tracked data
        this.cleanupOldData();
        // Force garbage collection if available
        if (typeof window !== 'undefined' && 'gc' in window) {
            window.gc();
        }
    }
    /**
     * Check if memory is suitable for quiz generation
     */
    isMemorySuitableForQuizGeneration() {
        const usage = this.getCurrentMemoryUsage();
        const usedMB = usage.used / (1024 * 1024);
        // Return true if memory usage is below warning threshold
        return usedMB < this.thresholds.warning;
    }
}
// Global instance
export const memoryManager = new MemoryManager();
// Convenience functions
export const getMemoryUsage = () => {
    return memoryManager.getCurrentMemoryUsage();
};
export const canHandleFile = (fileSize) => {
    return memoryManager.canHandleFileSize(fileSize);
};
export const trackPdfData = (data, identifier) => {
    return memoryManager.trackPdfData(data, identifier);
};
export const releasePdfData = (data, identifier) => {
    return memoryManager.releasePdfData(data, identifier);
};
export const registerReferenceCleanup = (callback) => {
    return memoryManager.registerReferenceCleanup(callback);
};
export const optimizeForReferenceViewer = () => {
    memoryManager.optimizeForReferenceViewer();
};
export const getReferenceViewerMemoryStatus = () => {
    return memoryManager.getReferenceViewerMemoryStatus();
};
export const trackBodyHtml = (referenceId, byteLength) => {
    return memoryManager.trackBodyHtml(referenceId, byteLength);
};
export const releaseBodyHtml = (referenceId) => {
    return memoryManager.releaseBodyHtml(referenceId);
};
export const getMemoryStats = () => {
    return memoryManager.getStats();
};
// React hook for memory monitoring
export const useMemoryMonitoring = () => {
    const [usage, setUsage] = React.useState(memoryManager.getCurrentMemoryUsage());
    const [stats, setStats] = React.useState(memoryManager.getStats());
    React.useEffect(() => {
        const updateMemoryInfo = () => {
            setUsage(memoryManager.getCurrentMemoryUsage());
            setStats(memoryManager.getStats());
        };
        // Update immediately
        updateMemoryInfo();
        // Update every 5 seconds
        const interval = setInterval(updateMemoryInfo, 5000);
        return () => clearInterval(interval);
    }, []);
    return {
        usage,
        stats,
        isHighUsage: usage.percentage > 70,
        isCriticalUsage: usage.percentage > 90,
        formatBytes: (bytes) => {
            if (bytes === 0)
                return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        }
    };
};
